/**
 * Revoke Key Modal
 * 
 * Confirmation modal before revoking an API key.
 * 
 * @module components/keys/RevokeKeyModal
 */

import { useState } from 'react';
import { Modal, Button, Alert } from '@/components/ui';

// =============================================================================
// Types
// =============================================================================

export interface RevokeKeyModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  /** The key label */
  keyLabel: string;
  /** The key display string (e.g., "hb_live_...x4Kp") */
  keyDisplay: string;
}

// =============================================================================
// Component
// =============================================================================

export function RevokeKeyModal({
  open,
  onClose,
  onConfirm,
  keyLabel,
  keyDisplay,
}: RevokeKeyModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Revoke API Key"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirm} loading={loading}>
            Revoke Key
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert variant="warning">
          Are you sure you want to revoke this key?
        </Alert>

        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="font-medium text-gray-900">{keyLabel}</p>
          <p className="text-sm text-gray-500 font-mono">{keyDisplay}</p>
        </div>

        <p className="text-sm text-gray-500">
          This action cannot be undone. Any applications using this key will
          immediately lose access to all APIs.
        </p>

        {error && (
          <Alert variant="error">{error}</Alert>
        )}
      </div>
    </Modal>
  );
}
